import { useTemplates } from '@/contexts/useTemplates'
import {
  Combobox,
  Field,
  HStack,
  Highlight,
  Portal,
  Tag,
  Text,
  useComboboxContext,
  useFilter,
  useListCollection,
} from '@chakra-ui/react'
import { type UIEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type TemplateOption = {
  label: string // template name
  value: string // template ID
}

interface TemplateComboboxProps {
  value?: string
  onChange?: (value: string | null) => void
  error?: string
  required?: boolean
  label?: string
  id?: string
  disabled?: boolean
  onBlur?: () => void
}

export const TemplateCombobox = ({
  value,
  onChange,
  error,
  required = false,
  label,
  id = 'template-id',
  disabled = false,
  onBlur,
}: TemplateComboboxProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { templatesState, loadMoreTemplates } = useTemplates()
  const [selected, setSelected] = useState<string | null>(value ?? null)
  const templatesLength = useRef(0)

  const defaultLabel = label ?? t('fields.template')

  const templateOptions: TemplateOption[] = useMemo(
    () =>
      templatesState.data.map((template) => ({
        label: template.name || template.template_id,
        value: template.template_id,
      })),
    [templatesState.data],
  )

  const { contains } = useFilter({ sensitivity: 'base' })

  const { collection, filter, set } = useListCollection({
    initialItems: templateOptions,
    filter: contains,
    //   This is necessary for the filter to include template ID as well as name
    itemToString: (item) => `${item.value}-${item.label}`,
  })

  // Handle scroll to load more templates
  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget
    const scrolledToBottom =
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50

    if (
      scrolledToBottom &&
      templatesState.hasNext &&
      !templatesState.isLoading
    ) {
      loadMoreTemplates()
    }
  }

  const selectedOption = selected
    ? templateOptions.find(({ value }) => value === selected)
    : null

  useEffect(() => {
    if (
      !templatesState.isLoading &&
      templatesLength.current !== templatesState.data.length
    ) {
      // Set items after each load
      set(templateOptions)
      templatesLength.current = templatesState.data.length
    }
  }, [
    templateOptions,
    templatesState.isLoading,
    templatesState.data.length,
    set,
  ])

  const isInvalid = Boolean(error)

  return (
    <Field.Root required={required} invalid={isInvalid} disabled={disabled}>
      <Combobox.Root
        collection={collection}
        value={value ? [value] : undefined}
        onValueChange={(option) => {
          const newValue = option.value.length ? option.value[0] : null
          setSelected(newValue)
          onChange?.(newValue)
          onBlur?.()
        }}
        onInputValueChange={(e) => filter(e.inputValue)}
        disabled={
          (templatesState.isLoading && !templatesState.data.length) || disabled
        }
        data-field={id}
      >
        <Combobox.Label htmlFor={id}>
          {label || defaultLabel}
          <Field.RequiredIndicator ml={1} />
        </Combobox.Label>
        <Combobox.Control>
          {selectedOption ? (
            <SelectedValueWithTag
              selectedOption={selectedOption}
              disabled={disabled}
            />
          ) : (
            <Combobox.Input
              backgroundColor="bg"
              borderColor={isInvalid ? 'content.error' : 'border'}
              onBlur={onBlur}
              placeholder={
                templatesState.isLoading
                  ? String(t('templateCombobox.loading'))
                  : String(t('templateCombobox.placeholder'))
              }
            />
          )}
          {!disabled && (
            <Combobox.IndicatorGroup>
              {selected && <Combobox.ClearTrigger />}
              <Combobox.Trigger />
            </Combobox.IndicatorGroup>
          )}
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner>
            <Combobox.Content
              maxHeight="300px"
              overflowY="auto"
              onScroll={handleScroll}
            >
              <Combobox.Empty>
                {t('templateCombobox.noTemplatesFound')}
              </Combobox.Empty>
              {collection.items.map((item) => (
                <ComboboxItem key={item.value} item={item} />
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
      <Field.ErrorText position="absolute" bottom="-1.2rem">
        {error}
      </Field.ErrorText>
    </Field.Root>
  )
}

function ComboboxItem(
  props: Readonly<{ item: { label: string; value: string } }>,
) {
  const { item } = props
  const combobox = useComboboxContext()
  return (
    <Combobox.Item item={item} key={item.value}>
      <Combobox.ItemText>
        <Tag.Root size="sm" mr={2}>
          <Tag.Label>
            <Highlight
              ignoreCase
              query={combobox.inputValue}
              styles={{ backgroundColor: 'yellow', fontWeight: 'medium' }}
            >
              {item.value}
            </Highlight>
          </Tag.Label>
        </Tag.Root>
        <Highlight
          ignoreCase
          query={combobox.inputValue}
          styles={{ backgroundColor: 'yellow', fontWeight: 'medium' }}
        >
          {item.label}
        </Highlight>
      </Combobox.ItemText>
    </Combobox.Item>
  )
}

function SelectedValueWithTag({
  selectedOption,
  disabled = false,
}: Readonly<{ selectedOption: TemplateOption | null; disabled?: boolean }>) {
  if (!selectedOption) {
    return null
  }

  return (
    <HStack
      gap={2}
      h={10}
      borderRadius="sm"
      borderWidth={1}
      alignItems="center"
      borderColor="border"
      backgroundColor="bg"
      opacity={disabled ? 0.5 : 1}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      pl={2}
    >
      <Tag.Root size="sm">
        <Tag.Label>{selectedOption.value}</Tag.Label>
      </Tag.Root>
      <Text
        fontSize="sm"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxWidth="calc(100% - 6rem)"
      >
        {selectedOption.label}
      </Text>
    </HStack>
  )
}
